import React, {useState} from 'react';
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {ProductAPIDataSource} from '../api';
import {BrandDTO} from '../models/brand.dto';
import {CreateBrandDTO, UpdateBrandDTO} from '../models/brand.dto';
import AppTable, {TableColumn} from '../../../Presentation/components/ui/AppTable';
import Button from '../../../Presentation/components/ui/Button';
import Modal from '../../../Presentation/components/ui/Modal';
import BrandForm from './BrandForm';
import {useNotification} from '../../../Services/useNotification';
import StandardizedErrorHandler from '../../../Services/common/StandardizedErrorHandler';

const BrandList: React.FC = () => {
  const queryClient = useQueryClient();
  const {success: showSuccess} = useNotification();
  const {data: brands = [], isLoading, error} = useQuery({queryKey: ['brands'], queryFn: ProductAPIDataSource.getBrands});

  // حالة المودال
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [selectedBrand, setSelectedBrand] = useState<BrandDTO | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // إضافة براند
  const createMutation = useMutation({
    mutationFn: (data: CreateBrandDTO) => ProductAPIDataSource.createBrand(data),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['brands']});
      setModalOpen(false);
      showSuccess('تمت إضافة البراند بنجاح');
    },
    onError: (error: Error) => {
      StandardizedErrorHandler.getInstance().handleError(error);
    }
  });

  // تعديل براند
  const updateMutation = useMutation({
    mutationFn: ({id, data}: { id: number; data: UpdateBrandDTO }) => ProductAPIDataSource.updateBrand(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['brands']});
      setModalOpen(false);
      setSelectedBrand(null);
      showSuccess('تم تعديل البراند بنجاح');
    },
    onError: (error: Error) => {
      StandardizedErrorHandler.getInstance().handleError(error);
    }
  });

  // حذف براند
  const deleteMutation = useMutation({
    mutationFn: (id: number) => ProductAPIDataSource.deleteBrand(id),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['brands']});
      setDeleteId(null);
      showSuccess('تم حذف البراند بنجاح');
    },
    onError: (error: Error) => {
      StandardizedErrorHandler.getInstance().handleError(error);
    }
  });

  const handleAdd = (data: CreateBrandDTO) => {
    setSubmitting(true);
    createMutation.mutate(data, {
      onSettled: () => setSubmitting(false)
    });
  };

  const handleEdit = (data: UpdateBrandDTO) => {
    if (!selectedBrand) {return;}
    setSubmitting(true);
    updateMutation.mutate({id: selectedBrand.id, data}, {
      onSettled: () => setSubmitting(false)
    });
  };

  const handleDelete = (id: number) => {
    setDeleteLoading(true);
    deleteMutation.mutate(id, {
      onSettled: () => setDeleteLoading(false)
    });
  };

  const columns: TableColumn<BrandDTO>[] = [
    {key: 'id', title: 'ID', render: (row) => row.id},
    {key: 'name', title: 'اسم البراند', render: (row) => row.name},
    {key: 'logo_url', title: 'الشعار', render: (row) => row.logo_url ? (
      <img src={row.logo_url} alt={row.name} className="w-8 h-8 object-contain" />
    ) : '-'},
    {key: 'description', title: 'الوصف', render: (row) => row.description || '-'},
    {key: 'website', title: 'الموقع', render: (row) => row.website ? (
      <a href={row.website} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
        {row.website}
      </a>
    ) : '-'}
  ];

  if (error) {
    return <div className="text-red-600 text-center py-8">حدث خطأ أثناء جلب البراندات</div>;
  }

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button onClick={() => { setModalMode('add'); setSelectedBrand(null); setModalOpen(true); }}>
          إضافة براند جديد
        </Button>
      </div>
      <AppTable
        columns={columns}
        data={brands}
        rowKey={(row, index) => `brand-${row.id}-${index || 0}`}
        emptyText={isLoading ? 'جاري التحميل...' : 'لا توجد براندات'}
        actions={row => [
          {label: 'تعديل', onClick: () => { setModalMode('edit'); setSelectedBrand(row); setModalOpen(true); }},
          {label: 'حذف', onClick: () => setDeleteId(row.id), danger: true}
        ]}
        hidePagination={true}
      />
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={modalMode === 'add' ? 'إضافة براند جديد' : 'تعديل البراند'}>
        <BrandForm
          key={modalMode === 'edit' && selectedBrand ? `edit-${selectedBrand.id}` : 'add'}
          initialData={modalMode === 'edit' && selectedBrand ? {
            name: selectedBrand.name,
            logo_url: selectedBrand.logo_url,
            description: selectedBrand.description,
            website: selectedBrand.website
          } : {}}
          onSubmit={(data) => {
            if (modalMode === 'add') {
              handleAdd(data as CreateBrandDTO);
            } else {
              handleEdit(data as UpdateBrandDTO);
            }
          }}
          onCancel={() => setModalOpen(false)}
          submitting={submitting}
          isEdit={modalMode === 'edit'}
        />
      </Modal>
      <Modal open={deleteId !== null} onClose={() => setDeleteId(null)} title="تأكيد الحذف">
        <div className="text-center mb-6">
          هل أنت متأكد أنك تريد حذف هذا البراند؟
        </div>
        <div className="flex gap-4 justify-center">
          <Button variant="ghost" onClick={() => setDeleteId(null)} disabled={deleteLoading}>
            إلغاء
          </Button>
          <Button variant="danger" onClick={() => handleDelete(deleteId!)} isLoading={deleteLoading}>
            حذف
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default BrandList;
